/**
 * Cambio de la propia clave (ADM-01).
 *
 * Es tambien el paso obligatorio de quien entra con la clave temporal que le
 * genero el sistema. Por eso usa el mismo armazon suelto de la vista de acceso
 * y no el del panel: mientras no cambie la clave, el resto del backoffice no le
 * responde.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { Icono } from '../../componentes/comunes/Icono';
import { useSesion } from '../../contexto/sesion-contexto';
import { Seo } from '../../seo/Seo';
import { cambiarClave } from '../../servicios/autenticacion.servicio';
import { ErrorDeApi } from '../../servicios/cliente-api';
import { rutas } from '../../utilidades/rutas';

export function CambiarClave() {
  const { estado, administrador, actualizar } = useSesion();
  const navegar = useNavigate();

  const [claveActual, setClaveActual] = useState('');
  const [claveNueva, setClaveNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (estado === 'fuera') {
    return <Navigate to={rutas.administracion.acceso()} replace />;
  }

  async function enviar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setError(null);

    if (claveNueva !== repetida) {
      setError('Las dos claves nuevas no son iguales.');
      return;
    }

    setEnviando(true);

    try {
      actualizar(await cambiarClave({ claveActual, claveNueva }));
      navegar(rutas.administracion.panel(), { replace: true });
    } catch (fallo) {
      setError(
        fallo instanceof ErrorDeApi
          ? (fallo.detalles?.[0]?.mensaje ?? fallo.message)
          : 'No pudimos conectarnos con el servidor. Intenta de nuevo.',
      );
      setEnviando(false);
    }
  }

  const esObligatorio = administrador?.debeCambiarClave === true;

  return (
    <>
      <Seo
        titulo="Cambiar la clave"
        descripcion="Cambio de clave del panel de administración."
        ruta={rutas.administracion.clave()}
        sinIndexar
      />

      <main className="acceso">
        <div className="acceso-caja">
          <div className="acceso-marca">
            <span className="acceso-icono">
              <Icono nombre="llave" />
            </span>
          </div>

          <h1>{esObligatorio ? 'Elige tu clave' : 'Cambiar mi clave'}</h1>
          <p className="acceso-bajada">
            {esObligatorio
              ? 'Entraste con una clave temporal. Elige una tuya para poder usar el panel.'
              : 'Necesitas tu clave actual para poder cambiarla.'}
          </p>

          <form
            onSubmit={(evento) => {
              void enviar(evento);
            }}
            noValidate
          >
            {error !== null ? (
              <p className="acceso-error" role="alert">
                <Icono nombre="alerta" />
                <span>{error}</span>
              </p>
            ) : null}

            <div className="campo">
              <label htmlFor="clave-actual">{esObligatorio ? 'Clave temporal' : 'Clave actual'}</label>
              <input
                id="clave-actual"
                type="password"
                autoComplete="current-password"
                required
                value={claveActual}
                onChange={(evento) => {
                  setClaveActual(evento.target.value);
                }}
              />
            </div>

            <div className="campo">
              <label htmlFor="clave-nueva">Clave nueva</label>
              <input
                id="clave-nueva"
                type="password"
                autoComplete="new-password"
                required
                minLength={10}
                aria-describedby="ayuda-clave"
                value={claveNueva}
                onChange={(evento) => {
                  setClaveNueva(evento.target.value);
                }}
              />
              <p className="campo-ayuda" id="ayuda-clave">
                Al menos 10 caracteres. Una frase que recuerdes sirve mejor que ocho caracteres
                raros.
              </p>
            </div>

            <div className="campo">
              <label htmlFor="clave-repetida">Repite la clave nueva</label>
              <input
                id="clave-repetida"
                type="password"
                autoComplete="new-password"
                required
                value={repetida}
                onChange={(evento) => {
                  setRepetida(evento.target.value);
                }}
              />
            </div>

            <button type="submit" className="btn btn-primario btn-lg btn-bloque" disabled={enviando}>
              {enviando ? 'Guardando…' : 'Guardar la clave'}
            </button>
          </form>
        </div>
      </main>
    </>
  );
}
